import _ from 'lodash';
import moment from 'moment-timezone';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAsyncEffect } from 'rooks';
import { sqWorkbenchStore, sqWorkbookStore } from '@/core/core.stores';
import { useFluxPath } from '@/core/hooks/useFluxPath.hook';
import { SaveAndContinueFormBuilder } from '@/formbuilder/SaveAndContinueFormBuilder.page';
import { ImpactReportSlider } from '@/impactReports/ImpactReportSlider.atom';
import { KpiLabelWithDescription } from '@/impactReports/KpiLabelWithDescription.atom';
import { SelectedProjectDisplayAndLink } from '@/impactReports/SelectedProjectDisplayAndLink.atom';
import { SelectedWorksheetDisplayAndLink } from '@/impactReports/SelectedWorksheetDisplayAndLink.atom';
import { ImpactReportAIOverlay } from '@/impactReports/ValueCaptureAIOverlay.atom';
import {
  DAILY_FREQUENCY,
  MONTHLY_FREQUENCY,
  QUARTERLY_FREQUENCY,
  TRACK_IMPACT,
  WEEKLY_FREQUENCY,
  YEARLY_FREQUENCY,
  availableStages,
  instructions,
  stageIcons,
  toImpactReportSelectedItemType,
} from '@/impactReports/impactReport.types';
import {
  addOrUpdateImpactReport,
  giveEveryoneGroupReadPermissions,
  removeEveryoneGroupReadPermissions,
  resolveImpactReportReviewSave,
  stageDisplayStringByIndex,
  toSavedImpactReportReviewState,
  verifyEveryoneGroupReadPermissions,
  withImpactReportReview,
} from '@/impactReports/impactReport.utilities';
import {
  getCustomCategoryTitle,
  getKPIDisplayName,
  getKPIExpectedAnnualColumnName,
  getKPIUnit,
} from '@/impactReports/impactReportDisplay.utilities';
import {
  changeImpactReportKpiMode,
  loadImpactReportDraft,
  serializeImpactReportDraft,
} from '@/impactReports/impactReportForm.utilities';
import {
  useImpactReportActions,
  useImpactReportExpectedAnnualKpiColumns,
  useImpactReportExpectedAnnualKpiVisibility,
  useImpactReportKpiColumns,
  useImpactReportShowAssetPath,
  useImpactReportShowLocation,
  useImpactReportShowSite,
  useImpactReportTableId,
} from '@/impactReports/impactReports.store';
import { useImpactReportApiActions } from '@/impactReports/useImpactReportApiActions.hook';
import { useImpactReportCategorySave } from '@/impactReports/useImpactReportCategorySave.hook';
import { ReportContentWorksheetAndUrl } from '@/reportEditor/components/reportContentModal/shared/ReportContentWorksheetAndUrl.molecule';
import { hasAnalyticsAdminCapability } from '@/services/authorization.service';
import { genAIEnabled } from '@/services/systemConfiguration.utilities';
import { doTrack } from '@/track/track.service';
import { randomUUID } from '@/utilities/guid.utilities';
import { errorToast } from '@/utilities/toast.utilities';
import { SelectWorksheet } from '@/worksheets/SelectWorksheet.molecule';
import { sqWorkbooksApi } from '@seeqdev/api/endpoint/WorkbooksApi';
import { SeeqNames } from '@seeqdev/constants';
import { Alert, Button, Icon } from '@seeqdev/qomponents';
import type { Dispatch, SetStateAction } from 'react';
import type { FormElement } from '@/formbuilder/formBuilder.constants';

import type { ImpactReportCategory, ImpactReportSelectedItemType, Location } from '@/impactReports/impactReport.types';
import type { SavedImpactReportReviewState } from '@/impactReports/impactReport.utilities';
import type { ImpactReportDraft } from '@/impactReports/impactReportForm.utilities';
import type { SelectedSearchItem } from '@/search/search.types';
import type { ScalarPropertyV1 } from '@seeqdev/api/model/ScalarPropertyV1';

interface ImpactReportModalProps {
  existingReport?: string;
  stageIndex?: number;
  workbookId?: string;
  worksheetId?: string;
  selectedItemType: ImpactReportSelectedItemType;
  setWorkbookId: (id: string) => void;
  setWorksheetId: (id: string) => void;
  setSelectedItemType: Dispatch<SetStateAction<ImpactReportSelectedItemType>>;
  closeFn: (reloadTable?: boolean) => void;
  setStageIndex: (idx: number) => void;
  report?: Record<string, any>;
  categories?: ImpactReportCategory[];
}

interface ImpactReportHeaderProps {
  workbookId: string;
  worksheetId: string;
  selectedItemType: ImpactReportSelectedItemType;
  stageIndex: number;
  setStageIndex: (idx: number) => void;
  setWorkbookId: (id: string) => void;
  setWorksheetId: (id: string) => void;
  setSelectedItemType: Dispatch<SetStateAction<ImpactReportSelectedItemType>>;
}

const validSuffixes = ['/h', '/d', '/min', '/mo', '/ms', '/ns', '/s', '/wk', '/y', '']; // '' for just the unit itself, e.g. '$'

function isValidUnit(unit: string): boolean {
  return validSuffixes.some((suffix) => unit.endsWith(suffix));
}

const getValidUnitForDisplay = (unit: string) => (unit.indexOf('·') > 0 ? unit.substring(0, unit.indexOf('·')) : unit);

export const ImpactReportHeader = ({
  workbookId,
  worksheetId,
  selectedItemType,
  stageIndex = 0,
  setStageIndex,
  setWorksheetId,
  setWorkbookId,
  setSelectedItemType,
}: ImpactReportHeaderProps) => {
  const { t } = useTranslation();
  const currentStage = availableStages[stageIndex];
  const currentStageDisplay = t(`IMPACT_REPORT.${currentStage?.toUpperCase()}`);
  const dataLabProjectSelected = selectedItemType === SeeqNames.Types.Project;

  return (
    <div className="flexRowContainer valueCapture width-maximum">
      <div className="tw:mb-sq-5">
        <h4>{t('IMPACT_REPORT.CAPTURE_IMPACT')}</h4>
      </div>
      <div className="flexColumnContainer tw:flex-wrap">
        {workbookId && dataLabProjectSelected ? (
          <div className="flexRowContainer flexFill tw:mt-0 tw:items-start">
            <div>{t('IMPACT_REPORT.SELECTED_PROJECT')}</div>
            <div>
              <SelectedProjectDisplayAndLink projectId={workbookId} />
              <Icon
                icon="fa-close"
                onClick={() => {
                  setWorkbookId('');
                  setWorksheetId('');
                  setSelectedItemType('');
                  setStageIndex(0);
                }}
                extraClassNames="ml10 mt3 cursorPointer"
              />
            </div>
          </div>
        ) : workbookId && worksheetId ? (
          <div className="flexRowContainer flexFill tw:mt-0 tw:items-start">
            <div>{t('IMPACT_REPORT.SELECTED_WORKSHEET')}</div>
            <div>
              <SelectedWorksheetDisplayAndLink workbookId={workbookId} worksheetId={worksheetId} />
              <Icon
                icon="fa-close"
                onClick={() => {
                  setWorkbookId('');
                  setWorksheetId('');
                  setSelectedItemType('');
                  setStageIndex(0);
                }}
                extraClassNames="ml10 mt3 cursorPointer"
              />
            </div>
          </div>
        ) : (
          <div className="flexFill" />
        )}
        {currentStage && (
          <div className="flexRowContainer tw:items-end">
            <h6 className="mtn8">
              {t('IMPACT_REPORT.CURRENT_STAGE')} {currentStageDisplay}
            </h6>
            <ImpactReportSlider setStageIdx={setStageIndex} stageIndex={stageIndex} />
          </div>
        )}
      </div>
    </div>
  );
};

export const ImpactReportForm = ({
  stageIndex = 0,
  workbookId,
  worksheetId,
  selectedItemType,
  setWorkbookId,
  setWorksheetId,
  setSelectedItemType,
  setStageIndex,
  closeFn,
  report,
  categories,
}: ImpactReportModalProps) => {
  const isTopic = useFluxPath(sqWorkbookStore, () => sqWorkbookStore.isTopic);
  const currentUser = useFluxPath(sqWorkbenchStore, () => sqWorkbenchStore.currentUser);
  const canReviewReports = hasAnalyticsAdminCapability();
  const kpiColumns = useImpactReportKpiColumns();
  const expectedAnnualKpiColumns = useImpactReportExpectedAnnualKpiColumns();
  const expectedAnnualKpiVisibility = useImpactReportExpectedAnnualKpiVisibility();
  const showSite = useImpactReportShowSite();
  const showLocation = useImpactReportShowLocation();
  const showAssetPath = useImpactReportShowAssetPath();
  const { setUpdateNominations } = useImpactReportActions();
  const { t } = useTranslation();
  const [rowDatumId, setRowDatumId] = useState<string | undefined>(
    report && report[SeeqNames.MaterializedTables.DatumIdColumn],
  );
  const [draft, setDraft] = useState<ImpactReportDraft>(() => ({
    title: '',
    description: genAIEnabled() ? '' : instructions,
    owner: { id: currentUser.id, name: currentUser.name },
    site: currentUser.site ?? '',
    asset: '',
    impactType: 'oneTime',
    // New reports default to today; the dashboard viewing range is a separate concern.
    impactStartDate: moment(),
    impactFrequency: '',
    kpiValues: {},
    expectedAnnualValues: {},
    categoryValues: {},
    reviewComment: '',
  }));
  const updateDraft = (changes: Partial<ImpactReportDraft>) => setDraft((previous) => ({ ...previous, ...changes }));
  const {
    title,
    description,
    owner,
    site,
    asset,
    impactType,
    impactStartDate,
    impactEndDate,
    impactFrequency,
    location,
    kpiValues: customKPIValues,
    expectedAnnualValues: customKPIExpectedAnnualValues,
    categoryValues: customCategories,
    reviewComment,
  } = draft;
  const saveCategories = useImpactReportCategorySave(report, categories);
  const [reviewApprovalConfirmed, setReviewApprovalConfirmed] = useState(false);
  // The report prop is never refreshed while the modal is open, so the review state has to be tracked as it is written.
  const [savedReview, setSavedReview] = useState<SavedImpactReportReviewState>(() =>
    toSavedImpactReportReviewState(report),
  );
  // Undefined until the report has loaded; thereafter what the row holds, including saves made in this session.
  const [savedFieldValues, setSavedFieldValues] = useState<ReturnType<typeof serializeImpactReportDraft>>();
  const [forceUpdateText, setForceUpdateText] = useState(description);
  const dataLabProjectSelected = selectedItemType === SeeqNames.Types.Project;
  const isVantage = selectedItemType === SeeqNames.Types.Vantage;
  const [hasReadPermissions, setHasReadPermissions] = useState(false);
  const [savedReadPermissions, setSavedReadPermissions] = useState(false);

  const impactFrequencyOptions = [
    { value: DAILY_FREQUENCY, label: t('IMPACT_REPORT.FREQUENCIES.DAILY') },
    { value: WEEKLY_FREQUENCY, label: t('IMPACT_REPORT.FREQUENCIES.WEEKLY') },
    { value: MONTHLY_FREQUENCY, label: t('IMPACT_REPORT.FREQUENCIES.MONTHLY') },
    { value: QUARTERLY_FREQUENCY, label: t('IMPACT_REPORT.FREQUENCIES.QUARTERLY') },
    { value: YEARLY_FREQUENCY, label: t('IMPACT_REPORT.FREQUENCIES.YEARLY') },
  ];

  useAsyncEffect(async () => {
    if (!workbookId) return;
    const everyoneCanRead = await verifyEveryoneGroupReadPermissions(workbookId);
    setHasReadPermissions(everyoneCanRead);
    setSavedReadPermissions(everyoneCanRead);
  }, [workbookId]);

  useAsyncEffect(async () => {
    if (!workbookId || selectedItemType) return;
    const workbookResponse = await sqWorkbooksApi.getWorkbook({ id: workbookId });
    setSelectedItemType(toImpactReportSelectedItemType(workbookResponse.data?.type));
  }, [workbookId, selectedItemType]);

  const [showAIOverlay, setShowAIOverlay] = useState(description === '');
  const tableId = useImpactReportTableId();
  const { loadImpactReportTableId } = useImpactReportApiActions();
  const supportsAI = Boolean(workbookId && worksheetId && !isVantage && !dataLabProjectSelected);

  useAsyncEffect(async () => {
    await loadImpactReportTableId();
  }, []);

  useEffect(() => {
    setShowAIOverlay(supportsAI && description === '');
    if (!supportsAI && workbookId && !description) {
      setForceUpdateText(instructions);
    }
  }, [supportsAI, workbookId, description]);

  useEffect(() => {
    if (!report || _.isEmpty(report)) return;
    const loaded = loadImpactReportDraft(report, kpiColumns, expectedAnnualKpiColumns, categories);
    setDraft(loaded);
    setSavedFieldValues(serializeImpactReportDraft(loaded));
    setSavedReview(toSavedImpactReportReviewState(report));
    setWorksheetId(loaded.worksheetId ?? '');
    setWorkbookId(loaded.workbookId ?? '');
    setSelectedItemType(toImpactReportSelectedItemType(report[SeeqNames.ImpactReport.ImpactBasedOn]));
  }, [categories, expectedAnnualKpiColumns, kpiColumns, report, setSelectedItemType, setWorkbookId, setWorksheetId]);

  const currentStage = availableStages[stageIndex];
  const fieldValues = serializeImpactReportDraft({ ...draft, workbookId, worksheetId });
  // Until the report has loaded there is nothing to compare against, and a new report has no review to lose either way.
  const fieldsChanged = Boolean(savedFieldValues) && !_.isEqual(fieldValues, savedFieldValues);
  const changedSinceSave =
    fieldsChanged ||
    reviewComment !== (savedReview.review?.comment ?? '') ||
    savedReadPermissions !== hasReadPermissions ||
    savedReview.status !== currentStage;
  // Resolved per call so that a review is stamped with the time of the save that granted it.
  const resolveReviewSave = () =>
    resolveImpactReportReviewSave({
      canReviewReports,
      currentStage,
      saved: savedReview,
      reviewApprovalConfirmed,
      changedSinceSave,
      reviewComment,
      reviewedBy: currentUser.id,
      reviewDate: moment().toISOString(),
    });
  const willRevertReviewToQuantify = savedReview.status === 'review' && resolveReviewSave().status !== 'review';

  useEffect(() => {
    if (currentStage === 'review' || !reviewApprovalConfirmed) return;
    setReviewApprovalConfirmed(false);
  }, [currentStage, reviewApprovalConfirmed]);

  const sectionHeader = (stageIndex: number) => (
    <>
      <div
        className={`flexColumnContainer mbn16 formSectionHeader tw:items-center ${stageIndex !== 0 ? 'tw:mt-5' : ''}`}>
        <Icon icon={stageIcons[stageIndex]} extraClassNames="mr10 ml5" />
        <h4 className="flexFill">{stageDisplayStringByIndex(stageIndex)}</h4>
      </div>
      <hr />
    </>
  );

  if (!tableId) return null;

  const expectedAnnualColumnNames = new Set(expectedAnnualKpiColumns.map((column) => column.columnName));

  const kpiFields = kpiColumns
    ? kpiColumns.map((kpi) => {
        const value = customKPIValues[kpi.columnName];
        const expectedAnnualColumnName = getKPIExpectedAnnualColumnName(kpi.columnName);
        const includeExpectedAnnual =
          expectedAnnualKpiVisibility[kpi.columnName] && expectedAnnualColumnNames.has(expectedAnnualColumnName);
        const expectedAnnualValue = customKPIExpectedAnnualValues[expectedAnnualColumnName];
        const unit = getKPIUnit(kpi.columnName);
        const valueRowComponents: FormElement[] = [
          {
            component: 'ToggleInputSelectItemSearchFormComponent' as const,
            label: t('IMPACT_REPORT.PROVIDED_VALUE'),
            name: kpi.columnName,
            onChange: (item: string | SelectedSearchItem[] | number | null) => {
              if (typeof item === 'string' || typeof item === 'number') {
                updateDraft({ kpiValues: { ...customKPIValues, [kpi.columnName]: { mode: 'manual', amount: item } } });
              } else if (!item?.length) {
                updateDraft({ kpiValues: { ...customKPIValues, [kpi.columnName]: { mode: 'calculation' } } });
              } else if (item.length === 1) {
                const selectedItem = item[0];
                // verify the units - if they are invalid we have to make sure we let the user know or they can end
                // up with a very broken table :(
                const itemUnit =
                  'valueUnitOfMeasure' in selectedItem && typeof selectedItem.valueUnitOfMeasure === 'string'
                    ? selectedItem.valueUnitOfMeasure
                    : undefined;
                const kpiUnit = getKPIUnit(kpi.columnName);
                if (
                  (kpiUnit.indexOf('·') > -1 && itemUnit === kpiUnit.replace('·', '/')) ||
                  itemUnit === kpiUnit.substring(0, kpiUnit.indexOf('·')) ||
                  (itemUnit?.startsWith(kpiUnit) && isValidUnit(itemUnit))
                ) {
                  updateDraft({
                    kpiValues: {
                      ...customKPIValues,
                      [kpi.columnName]: { mode: 'calculation', item: { id: selectedItem.id } },
                    },
                  });
                  return;
                }
                errorToast({
                  messageKey: 'IMPACT_REPORT.INVALID_UNIT',
                  messageParams: { unit: getKPIUnit(kpi.columnName) },
                });
              }
            },
            unit: _.find(impactFrequencyOptions, { value: impactFrequency })?.label,
            required: false,
            showItemSelection: value?.mode === 'calculation',
            setShowItemSelection: (columnName: string, shouldShowSelection: boolean) => {
              updateDraft({
                kpiValues: {
                  ...customKPIValues,
                  [columnName]: changeImpactReportKpiMode(
                    customKPIValues[columnName],
                    shouldShowSelection ? 'calculation' : 'manual',
                  ),
                },
              });
            },
            size: 'sm' as const,
            testId: kpi.columnName,
            value: value?.mode === 'calculation' ? (value.item ? [value.item] : []) : (value?.amount ?? ''),
            isMultiple: false,
            notSelectedMessage: t('IMPACT_REPORT.FIND_CUSTOM_CALC'),
            title: t('IMPACT_REPORT.FIND_CUSTOM_CALC'),
            overrideWorkbookId: workbookId,
            scopeIds: [workbookId],
            searchTypes: ['Signal'],
            showToggle: impactType !== 'oneTime',
            iconPartialTooltipKey: 'IMPACT_REPORT.CUSTOM_CALC_TOOLTIP',
            customCalcInstructions: t('IMPACT_REPORT.CUSTOM_CALC_INSTRUCTIONS', {
              unit: getValidUnitForDisplay(getKPIUnit(kpi.columnName)),
            }),
          } as FormElement,
        ];

        if (includeExpectedAnnual) {
          valueRowComponents.push({
            component: 'TextFieldFormComponent',
            label: t('IMPACT_REPORT.EXPECTED_ANNUAL_SAVINGS'),
            name: expectedAnnualColumnName,
            value: expectedAnnualValue ?? '',
            onChange: (newValue) => {
              updateDraft({
                expectedAnnualValues: {
                  ...customKPIExpectedAnnualValues,
                  [expectedAnnualColumnName]: newValue as string,
                },
              });
            },
            size: 'sm',
            type: 'number',
            min: 0,
            testId: expectedAnnualColumnName,
            required: false,
            wrapperClassNames: 'width-30percent ml10',
          } as FormElement);
        }

        const components: FormElement[] = [
          {
            component: 'DisplayOnlyFormElementWrapper',
            name: `${kpi.columnName}-header`,
            children: (
              <div className="tw:mb-sq-5">
                <KpiLabelWithDescription
                  label={`${getKPIDisplayName(kpi.columnName)} (${unit})`}
                  description={kpi.description}
                />
              </div>
            ),
          },
          {
            component: 'FormRow',
            name: `${kpi.columnName}-values`,
            components: valueRowComponents,
          } as FormElement,
        ];

        return {
          component: 'FormGroup',
          name: `${kpi.columnName}-group`,
          formGroupExtraClassNames: 'flexColumnContainer p10 simpleBorder grayBorder roundedBorder mb10',
          components,
        } as FormElement;
      })
    : [];

  const categoryFields = categories
    ? categories.map((category) => {
        const cat = category.category;
        const fieldName = getCustomCategoryTitle(category);
        const options = category.options;
        const categoryId = cat.id!;
        return {
          label: fieldName,
          component: 'SelectFormComponent',
          name: categoryId,
          value: customCategories[categoryId],
          required: false,
          options: options as any,
          onChange: (value) => {
            updateDraft({
              categoryValues: { ...customCategories, [categoryId]: typeof value === 'string' ? value : undefined },
            });
          },
        } as FormElement;
      })
    : [];

  const updateCKContent = (content: string) => {
    updateDraft({ description: description + content });
    setForceUpdateText(description + content);
  };

  const formDefinition: FormElement[] = [
    {
      component: 'FormGroup',
      name: 'identify',
      includeIf: stageIndex >= 0,
      components: [
        {
          component: 'DisplayOnlyFormElementWrapper',
          name: 'identifyHeader',
          children: sectionHeader(0),
        },
        {
          component: 'TextFieldFormComponent',
          name: 'title',
          label: 'Title',
          value: title,
          required: true,
          onChange: (title) => {
            updateDraft({ title: title as string });
          },
          size: 'sm',
          testId: 'formTitle',
        },
        {
          component: 'DisplayOnlyFormElementWrapper',
          name: 'CKEditorLabel',
          children: [
            <div className="flexColumnContainer tw:mb-sq-5 tw:items-center" key="CKLabel">
              {t('IMPACT_REPORT.DESCRIPTION')}
              {genAIEnabled() && !showAIOverlay && worksheetId && workbookId && !isVantage && (
                <Icon
                  onClick={() => setShowAIOverlay(true)}
                  icon="fa-sparkles"
                  iconPrefix="fa-solid"
                  extraClassNames="ml5 cursorPointer"
                />
              )}
            </div>,
          ],
        },
        {
          component: 'CKEditorFormComponent',
          name: 'description',
          label: '',
          extraClassNames: 'width-maximum',
          value: description,
          required: true,
          onChange: (description) => {
            updateDraft({ description: description as string });
          },
          testId: 'description',
          forceUpdateText,
          overlay:
            genAIEnabled() && showAIOverlay && supportsAI ? (
              <ImpactReportAIOverlay
                onClose={() => setShowAIOverlay(false)}
                updateCKContent={updateCKContent}
                worksheetId={worksheetId}
                workbookId={workbookId}
              />
            ) : undefined,
          placeholder: '',
        },
        {
          component: 'SelectIdentityFormComponent',
          name: 'owner',
          idForLabel: 'useCaseOwner',
          label: 'Use case owner',
          extraClassNames: 'width-maximum mb25',
          value: owner.id ? owner : [],
          required: true,
          startEditable: true,
          onChange: (selectedIdentity) => {
            if (!Array.isArray(selectedIdentity)) {
              updateDraft({ owner: { id: selectedIdentity.id ?? '', name: selectedIdentity.name ?? '' } });
            }
          },
          testId: 'owner',
        },
        {
          component: 'CheckboxFormComponent',
          id: 'grantReadPermissions',
          name: 'grantReadPermissions',
          label: '',
          onChange: async () => {
            setHasReadPermissions(!hasReadPermissions);
          },
          skipToFormState: true,
          checkboxLabel: t('IMPACT_REPORT.READ_PERMISSIONS_REQUIRED'),
          value: hasReadPermissions,
        },
      ],
    },

    {
      component: 'FormGroup',
      name: 'categorize',
      includeIf: stageIndex >= 1,
      components: [
        {
          component: 'DisplayOnlyFormElementWrapper',
          name: 'categoryHeader',
          children: sectionHeader(1),
        },
        {
          component: 'LabelFormComponent',
          name: 'locationLabel',
          value: 'Location',
          includeIf: showLocation,
        },
        {
          component: 'LocationLookupFormComponent',
          name: 'locationLookup',
          extraClassNames: 'mb8',
          label: 'Location',
          value: location,
          onChange: (location: Location) => {
            updateDraft({ location });
          },
          testId: 'location',
          required: false,
          includeIf: showLocation,
        },
        {
          component: 'TextFieldFormComponent',
          name: 'site',
          label: 'Site',
          value: site,
          onChange: (site) => {
            updateDraft({ site: site as string });
          },
          size: 'sm',
          testId: 'site',
          includeIf: showSite,
        },
        ...categoryFields,
        {
          component: 'TextFieldFormComponent',
          name: 'asset',
          label: 'Asset type',
          value: asset,
          onChange: (asset) => {
            updateDraft({ asset: asset as string });
          },
          size: 'sm',
          testId: 'assetType',
          includeIf: showAssetPath,
        },
      ],
    },
    {
      component: 'FormGroup',
      name: 'quantify',
      includeIf: stageIndex >= 2,

      components: [
        {
          component: 'DisplayOnlyFormElementWrapper',
          name: 'quantifyHeader',
          children: sectionHeader(2),
        },
        {
          component: 'LabelFormComponent',
          name: 'impactType',
          value: 'Impact type',
          extraClassNames: '',
        },
        {
          component: 'FormRow',
          name: 'justARow',
          components: [
            {
              component: 'CheckboxFormComponent',
              name: 'oneTime',
              type: 'radio',
              value: impactType === 'oneTime',
              onChange: () => {
                updateDraft({
                  impactType: 'oneTime',
                  impactFrequency: '',
                  kpiValues: Object.fromEntries(
                    Object.entries(customKPIValues).map(([column, value]) => [
                      column,
                      changeImpactReportKpiMode(value, 'manual'),
                    ]),
                  ),
                });
              },
              checkboxLabel: 'One time impact',
              id: 'oneTime',
              testId: 'oneTime',
              skipToFormState: true,
              classes: 'flexAlignCenter mr20',
            },
            {
              component: 'CheckboxFormComponent',
              name: 'Continuous',
              type: 'radio',
              value: impactType === 'continuous',
              onChange: () => {
                updateDraft({ impactType: 'continuous' });
              },
              checkboxLabel: 'continuous impact',
              id: 'continuous',
              testId: 'continuous',
              classes: 'flexAlignCenter mr20',
            },
          ],
        },
      ],
    },
    {
      component: 'FormGroup',
      name: 'startEndDateRow',
      includeIf: stageIndex >= 2,
      components: [
        {
          includeIf: impactType !== 'customCalc',
          component: 'FormRow',
          name: 'startEndRow',
          extraClassNames: 'flexAlignCenter',
          components: [
            {
              component: 'DatePickerFormComponent',
              name: 'impactStartDate',
              label: t('IMPACT_REPORT.START_DATE'),
              value: impactStartDate,
              required: true,
              onChange: (date) => {
                updateDraft({ impactStartDate: date });
              },
              testId: 'impactStartDate',
              wrapperClassNames: 'mr5',
            },
            {
              component: 'DatePickerFormComponent',
              name: 'impactEndDate',
              includeIf: impactType === 'continuous',
              label: t('IMPACT_REPORT.END_DATE'),
              value: impactEndDate,
              onChange: (date) => {
                updateDraft({ impactEndDate: date });
              },
              testId: 'impactEndDate',
              wrapperClassNames: 'ml15',
            },
            {
              component: 'FormGroup',
              name: 'continuousImpact',
              includeIf: impactType === 'continuous',
              formGroupExtraClassNames: 'ml20 max-width-150',
              components: [
                {
                  label: t('IMPACT_REPORT.FREQUENCY'),
                  component: 'SelectFormComponent',
                  name: 'impactFrequency',
                  value: impactFrequency,
                  options: impactFrequencyOptions,
                  required: impactType === 'continuous',
                  onChange: (frequency) => {
                    updateDraft({ impactFrequency: frequency as string });
                  },
                },
              ],
            },
          ],
        },
        {
          component: 'FormRow',
          name: 'monetizeFormRow',
          components: [
            {
              component: 'FormGroup',
              name: 'kpis',
              components: [...kpiFields],
            },
          ],
        },
      ],
    },

    {
      component: 'DisplayOnlyFormElementWrapper',
      name: 'reviewedReportEditWarning',
      includeIf: willRevertReviewToQuantify,
      children: (
        <Alert
          dismissible={false}
          variant="warning"
          testId="reviewedReportEditWarning"
          extraClassNames="tw:mb-2 tw:mt-2">
          <span>
            <Icon icon="fa-warning m3" type="warning" tooltipPlacement="right" />{' '}
            {t('IMPACT_REPORT.EDIT_REVERTS_REVIEW')}
            {/* Only an admin has the toggle, and only on the Review stage where it is rendered below this warning. */}
            {canReviewReports && currentStage === 'review' && ` ${t('IMPACT_REPORT.EDIT_REVERTS_REVIEW_TOGGLE_HINT')}`}
          </span>
        </Alert>
      ),
    },
    {
      component: 'FormGroup',
      name: 'review',
      includeIf: currentStage === 'review',
      components: [
        {
          component: 'DisplayOnlyFormElementWrapper',
          name: 'reviewHeader',
          children: sectionHeader(3),
        },
        {
          component: 'TextFieldFormComponent',
          name: 'reviewComment',
          label: t('IMPACT_REPORT.REVIEW_COMMENT'),
          value: reviewComment,
          size: 'sm',
          as: 'textarea',
          rows: 4,
          onChange: (comment) => {
            updateDraft({ reviewComment: comment as string });
          },
          testId: 'reviewComment',
        },
        {
          component: 'ToggleFormComponent',
          name: 'reviewApprovalConfirmed',
          value: reviewApprovalConfirmed,
          onChange: () => {
            setReviewApprovalConfirmed((prev) => !prev);
          },
          iconSize: '2x',
          title: t('IMPACT_REPORT.REVIEW_APPROVAL_TOGGLE_TITLE'),
          label: t('IMPACT_REPORT.REVIEW_APPROVAL_TOGGLE_DESCRIPTION'),
          labelPosition: 'right',
          testId: 'reviewApprovalConfirmation',
        },
      ],
    },
  ];

  const onFormSubmitCallback = async () => {
    const properties: ScalarPropertyV1[] = Object.entries(fieldValues.properties).map(([name, value]) => ({
      name,
      value,
    }));
    const reviewSave = resolveReviewSave();

    const isContinuous = impactType === 'continuous';
    const hasManualSelection = Object.values(customKPIValues).some(
      (value) => value?.mode === 'manual' && value.amount !== '',
    );
    const unit = _.find(impactFrequencyOptions, { value: impactFrequency })?.label;
    if (isContinuous && hasManualSelection && !unit) {
      errorToast({
        messageKey: 'IMPACT_REPORT.FREQUENCY_REQUIRED',
        messageParams: { unit },
      });
      return Promise.reject();
    }
    // if we are updating an existing report, we need to use the existing datum id
    let useThisDatumId = rowDatumId;
    if (useThisDatumId === undefined) {
      doTrack(TRACK_IMPACT, 'Created new Impact Report', { isTopic, impactType });
      useThisDatumId = randomUUID();
      // Reserve the identity before either request so a retry continues the same report.
      setRowDatumId(useThisDatumId);
    } else {
      doTrack(TRACK_IMPACT, 'Updated existing Impact Report', { isTopic, impactType });
    }

    properties.push({ name: SeeqNames.MaterializedTables.ItemIdColumn, value: tableId });
    properties.push({ name: SeeqNames.MaterializedTables.DatumIdColumn, value: useThisDatumId });

    // CRAB-46894: Keep the legacy row identifier until the Impact Report table's datum-id rules are migrated.
    properties.push({ name: SeeqNames.MaterializedTables.UniqueRowId, value: useThisDatumId });

    await saveCategories(tableId, useThisDatumId, fieldValues.categoryValues, async () => {
      if (savedReview.status !== 'review') return;
      await addOrUpdateImpactReport(tableId, withImpactReportReview(properties, { status: 'quantify' }));
      // This transition survives a category or final-publication failure and must also survive a retry.
      setSavedReview({ status: 'quantify' });
    });

    await addOrUpdateImpactReport(tableId, withImpactReportReview(properties, reviewSave));
    setRowDatumId(useThisDatumId); // ensure this gets set so the "continue" button works
    setSavedReview(reviewSave);
    setSavedFieldValues(fieldValues);
    updateDraft({ reviewComment: reviewSave.review?.comment ?? '' });

    if (savedReadPermissions !== hasReadPermissions) {
      if (hasReadPermissions) {
        await giveEveryoneGroupReadPermissions(workbookId!);
      } else {
        await removeEveryoneGroupReadPermissions(workbookId!);
      }
      setSavedReadPermissions(hasReadPermissions);
    }
  };

  return (
    <div className="flexRowContainer flexFill valueCapture tw:mb-sq-10 tw:items-center">
      {!workbookId && !worksheetId && (
        <div className="flexRowContainer flexFill width-maximum tw:mt-sq-15 tw:items-center">
          <div className="flexColumnContainer mbn16 formSectionHeader width-maximum tw:mb-sq-10 tw:items-center">
            <h4 className="tw:ml-sq-10">{t('IMPACT_REPORT.SELECT_WORKSHEET_AND_WORKBOOK')}</h4>
          </div>
          <hr />
          <ReportContentWorksheetAndUrl
            selectOnlyWorkbooks={true}
            allowedTypes={[
              SeeqNames.Types.Topic,
              SeeqNames.Types.Analysis,
              SeeqNames.Types.Vantage,
              SeeqNames.Types.Folder,
              SeeqNames.Types.Project,
            ]}
            extraClassNames="width-maximum"
            setSelectedItem={(item) => {
              setSelectedItemType(toImpactReportSelectedItemType(item?.type));
              setWorkbookId(item?.id ?? '');
            }}
          />
          <Button label={t('IMPACT_REPORT.CONTINUE')} disabled={!workbookId} extraClassNames="mt10" />
        </div>
      )}
      {workbookId && !worksheetId && !dataLabProjectSelected && (
        <div className="flexRowContainer flexFill tw:mt-sq-15 tw:items-center">
          <div className="flexColumnContainer mbn16 formSectionHeader width-maximum tw:mb-sq-10 tw:items-center">
            <h4 className="tw:ml-sq-10">{t('IMPACT_REPORT.SELECT_WORKSHEET')}</h4>
          </div>
          <hr />
          <SelectWorksheet
            workbookId={workbookId}
            worksheetIds={[]}
            setWorksheetIds={(worksheetIds) => {
              if (worksheetIds?.length > 0) setWorksheetId(worksheetIds[0]);
            }}
            workstepIds={[]}
            setWorkstepIds={() => {}}
            setTotalWorksheets={() => {}}
            isLoading={false}
          />
          <Button label={t('IMPACT_REPORT.CONTINUE')} disabled={!worksheetId} extraClassNames="mt10" />
        </div>
      )}

      {workbookId && (worksheetId || dataLabProjectSelected) && (
        <SaveAndContinueFormBuilder
          submitBtnLabel={t('IMPACT_REPORT.SAVE_AND_CLOSE')}
          submitBtnId="saveAndClose"
          extraClassNames="flexFill tw:overflow-auto height-maximum"
          continueButtonLabel={t('IMPACT_REPORT.SAVE_AND_CONTINUE')}
          continueButtonPosition="center"
          continueButtonExtraClassNames="mr10"
          continueButtonEnabled={
            stageIndex < availableStages.length - 1 &&
            (availableStages[stageIndex + 1] !== 'review' || canReviewReports)
          }
          continueButtonOnSubmit={async () => {
            await onFormSubmitCallback();
            if (willRevertReviewToQuantify) {
              setStageIndex(availableStages.indexOf('quantify'));
            } else if (
              stageIndex < availableStages.length - 1 &&
              (availableStages[stageIndex + 1] !== 'review' || canReviewReports)
            ) {
              setStageIndex(stageIndex + 1);
            }
            doTrack(TRACK_IMPACT, 'Save and Continue Button', {
              stageIndex,
              changedSinceSave,
              willRevertReviewToQuantify,
            });
          }}
          continueButtonVariant="theme"
          formDefinition={formDefinition}
          submitFn={async () => {
            await onFormSubmitCallback();
            setRowDatumId(undefined);
            closeFn(true);
            setUpdateNominations(true);
            doTrack(TRACK_IMPACT, 'Save and Close Button', {
              stageIndex,
              changedSinceSave,
              willRevertReviewToQuantify,
            });
          }}
          closeFn={closeFn}
        />
      )}
    </div>
  );
};
